// src/components/Earnings.jsx
import React, { useEffect, useState, useRef } from "react";
import "./Earnings.css";
import EarningCards from "./EarningCards";
import { supabase } from "../supabaseClient";
import { useAuth } from "../contexts/SupabaseAuthProvider";

const RESET_HOURS = 24;
const PENDING_DELAY_MS = 30 * 1000; // 30 sec TEST MODE

function generatePuzzles(count) {
  const words = ["apple", "car", "chair", "smart", "money", "dream"];
  const emojis = ["🍎", "🚗", "📱", "⭐", "🐶", "🍕"];
  const puzzles = [];

  for (let i = 0; i < count; i++) {
    const type = Math.floor(Math.random() * 3);

    if (type === 0) {
      const a = Math.floor(Math.random() * 20) + 5;
      const b = Math.floor(Math.random() * 15) + 2;
      puzzles.push({
        id: Date.now() + i,
        type: "text",
        q: `What is ${a} + ${b}?`,
        ans: String(a + b),
        reward: 2,
        title: "Math Puzzle",
      });
    }

    if (type === 1) {
      const w = words[Math.floor(Math.random() * words.length)];
      puzzles.push({
        id: Date.now() + i,
        type: "text",
        q: `Reverse this word: ${w.toUpperCase()}`,
        ans: w.split("").reverse().join(""),
        reward: 2,
        title: "Reverse Word",
      });
    }

    if (type === 2) {
      const e = emojis[Math.floor(Math.random() * emojis.length)];
      puzzles.push({
        id: Date.now() + i,
        type: "text",
        q: `What does this emoji represent? ${e}`,
        ans: e === "🍎" ? "apple" : e === "🚗" ? "car" : "star",
        reward: 2,
        title: "Emoji Puzzle",
      });
    }
  }
  return puzzles;
}

export default function Earnings({ userPackage }) {
  const { user } = useAuth();

  const [system, setSystem] = useState({
    puzzles: [],
    index: 0,
    remaining: 0,
    solved: 0,
    totalEarned: 0,
    lastReset: 0,
    userPackage: 0,
  });

  const [history, setHistory] = useState([]);
  const [submission, setSubmission] = useState("");
  const [notif, setNotif] = useState(null);
  const [profitAnim, setProfitAnim] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  const processingRef = useRef(false);
  const intervalRef = useRef(null);

  const ding = new Audio(process.env.PUBLIC_URL + "/sounds/ding.mp3");
  const errorTone = new Audio(process.env.PUBLIC_URL + "/sounds/error.mp3");

  const packageLimits = {
    30: 1,
    50: 2,
    100: 5,
    200: 15,
    300: 20,
    500: 35,
    1000: 60,
    3000: 150,
    5000: 350,
  };

  const safeArray = (v) =>
    Array.isArray(v) ? v.filter((x) => x && typeof x === "object") : [];

  const notify = (msg, type = "success") => {
    setNotif({ msg, type });
    setTimeout(() => setNotif(null), 2000);
  };

  // LOAD USER DATA
  const loadData = async () => {
    if (!user?.id) return;

    const { data } = await supabase
      .from("users_data")
      .select("earn_system, earn_history, user_package, pending_profits")
      .eq("user_id", user.id)
      .maybeSingle();

    if (!data) return;

    const savedSystem = data.earn_system || {};
    const savedHistory = safeArray(data.earn_history);
    const limit = packageLimits[data.user_package] || 0;

    setHistory(savedHistory);

    if (!savedSystem.lastReset) {
      const fresh = generatePuzzles(limit);
      const newState = {
        puzzles: fresh,
        index: 0,
        remaining: limit,
        solved: 0,
        totalEarned: 0,
        lastReset: Date.now(),
        userPackage: data.user_package,
      };

      setSystem(newState);
      await supabase
        .from("users_data")
        .update({ earn_system: newState })
        .eq("user_id", user.id);

      return;
    }

    setSystem(savedSystem);
  };

  useEffect(() => {
    loadData();
    startPendingProcessor();
    return () => stopPendingProcessor();
  }, [user?.id]);

  // REALTIME SYNC
  useEffect(() => {
    if (!user?.id) return;
    const ch = supabase
      .channel("earn_rt")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "users_data", filter: `user_id=eq.${user.id}` },
        (payload) => {
          if (payload.new?.earn_system) setSystem(payload.new.earn_system);
          if (payload.new?.earn_history) setHistory(safeArray(payload.new.earn_history));
        }
      )
      .subscribe();

    return () => supabase.removeChannel(ch);
  }, []);

  // ADD PENDING ONLY
const addPendingProfit = async (amount) => {
  const { data } = await supabase
    .from("users_data")
    .select("pending_profits, earn_history")
    .eq("user_id", user.id)
    .maybeSingle();

  const pending = safeArray(data?.pending_profits);
  const historyArr = safeArray(data?.earn_history);

  const item = {
    id: "PP-" + Date.now(),
    amount,
    status: "pending",
    created_at: new Date().toISOString(),
  };

  await supabase
    .from("users_data")
    .update({
      pending_profits: [item, ...pending],
      earn_history: [
        {
          q: "Puzzle reward pending",
          reward: amount,
          time: new Date().toLocaleString(),
          timeISO: item.created_at,
          status: "pending",

          // 🔥 REQUIRED FOR BACKEND
          ref: item.id,
        },
        ...historyArr,
      ],
    })
    .eq("user_id", user.id);
};


  const cur = system.puzzles?.[system.index] || null;

  // FIXED REWARD LOGIC → instant totalEarned update, NO todays_profit ever
  const submit = async () => {
    if (!cur) return;
    if (!submission.trim()) {
      notify("Answer required", "error");
      errorTone.play();
      return;
    }

    if (submission.trim().toLowerCase() === cur.ans.toLowerCase()) {
      ding.play();
      notify("Correct! Pending reward added");

      await addPendingProfit(cur.reward);

      // INSTANT totalEarned add
      const patchedSystem = {
        ...system,
        totalEarned: Number(system.totalEarned || 0) + cur.reward,
      };
      setSystem(patchedSystem);

      await supabase
        .from("users_data")
        .update({ earn_system: patchedSystem })
        .eq("user_id", user.id);

      const newSystem = {
        ...patchedSystem,
        index: system.index + 1,
        remaining: system.remaining - 1,
        solved: system.solved + 1,
      };

      setSystem(newSystem);

      await supabase
        .from("users_data")
        .update({ earn_system: newSystem })
        .eq("user_id", user.id);
  } else {
  errorTone.play();
  notify("Wrong answer — skipped", "error");

  // auto-skip same as skip()
  const { data } = await supabase
    .from("users_data")
    .select("earn_history")
    .eq("user_id", user.id)
    .maybeSingle();

  const historyArr = safeArray(data?.earn_history);

  const newSystem = {
    ...system,
    index: system.index + 1,
    remaining: system.remaining - 1,
  };

  setSystem(newSystem);

  await supabase
    .from("users_data")
    .update({
      earn_system: newSystem,
      earn_history: [
        {
          q: cur.q,
          reward: 0,
          time: new Date().toLocaleString(),
          status: "skipped",
        },
        ...historyArr,
      ],
    })
    .eq("user_id", user.id);
}


    setSubmission("");
  };

  const skip = async () => {
    if (!cur) return;

    const { data } = await supabase
      .from("users_data")
      .select("earn_history")
      .eq("user_id", user.id)
      .maybeSingle();

    const historyArr = safeArray(data?.earn_history);

    const newSystem = {
      ...system,
      index: system.index + 1,
      remaining: system.remaining - 1,
    };

    setSystem(newSystem);

    await supabase
      .from("users_data")
      .update({
        earn_system: newSystem,
        earn_history: [
          {
            q: cur.q,
            reward: 0,
            time: new Date().toLocaleString(),
            status: "skipped",
          },
          ...historyArr,
        ],
      })
      .eq("user_id", user.id);

    setSubmission("");
  };

  const processPending = async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    const { data } = await supabase
      .from("users_data")
      .select("pending_profits, earn_history, earn_system")
      .eq("user_id", user.id)
      .maybeSingle();

    if (!data) {
      processingRef.current = false;
      return;
    }

    const pending = safeArray(data.pending_profits);
    const historyArr = safeArray(data.earn_history);

    if (!pending.length) {
      processingRef.current = false;
      return;
    }

    const now = Date.now();

    const eligible = pending.filter(
      (p) => now - Date.parse(p.created_at) >= PENDING_DELAY_MS
    );

    if (!eligible.length) {
      processingRef.current = false;
      return;
    }

    const newPending = pending.filter(
      (p) => !eligible.some((e) => e.id === p.id)
    );

    const updatedHistory = historyArr.map((h) => {
      const match = eligible.find(
        (e) => e.created_at === h.timeISO || e.created_at === h.created_at
      );
      if (match) return { ...h, status: "correct" };
      return h;
    });

    const newSystem = {
      ...(data.earn_system || {}),
    };

    await supabase
      .from("users_data")
      .update({
        pending_profits: newPending,
        earn_history: updatedHistory,
        earn_system: newSystem,
      })
      .eq("user_id", user.id);

    processingRef.current = false;
  };

  const startPendingProcessor = () => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(processPending, 5000);
  };

  const stopPendingProcessor = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  return (
    <div className="earnings-page">
      <div className="earnings-header">
        <h2>Earnings</h2>
        <button className="history-btn" onClick={() => setShowHistory(true)}>
          History
        </button>
      </div>

      {notif && <div className={`earn-notif ${notif.type}`}>{notif.msg}</div>}

      <EarningCards total={system.totalEarned} solved={system.solved} />

      <p className="remain-text">Remaining: {system.remaining}</p>

      {cur ? (
        <div className="puzzle-card">
          <h3>Puzzle #{system.index + 1}</h3>
          <p className="question">{cur.q}</p>

          <textarea
            value={submission}
            onChange={(e) => setSubmission(e.target.value)}
          />

          <button className="submit-btn" onClick={submit}>Submit</button>
          <button className="skip-btn" onClick={skip}>Skip</button>
        </div>
      ) : (
        <div className="placeholder">
          Daily puzzles completed — will reset after 24 hours.
        </div>
      )}

      {showHistory && (
        <div className="history-overlay">
          <div className="history-box">
            <h3>Puzzle History</h3>

            {history.length === 0 && (
              <p className="empty-history">No history yet.</p>
            )}

            <ul className="history-list">
              {history.map((h, i) => (
                <li key={i} className={h.status}>
                  <p className="q">
  {h.q === "Puzzle reward pending" ? "Puzzle reward" : h.q}
</p>

<p className="reward">
  {h.status === "correct"
    ? `+$${h.reward}`
    : h.status === "pending"
    ? `+$${h.reward} (pending)`
    : "Skipped"}
</p>

                  <p className="time">{h.time}</p>
                </li>
              ))}
            </ul>

            <button
              className="close-btn"
              onClick={() => setShowHistory(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
